import { Phone, Mail, MapPin, Clock } from "lucide-react";
import Hero from "../components/Hero";
import ContactForm from "../components/ContactForm";
import CTABanner from "../components/CTABanner";
import { IMAGES, SITE, COPY } from "../lib/_data";

export default function Contact() {
  const hero = COPY.contact.hero;
  const section = COPY.contact.section;
  const info = COPY.contact.info || {};
  const hoursCopy = COPY.contact.hours || {};
  const finalCta = COPY.contact.finalCta;
  return (
    <div data-testid="page-contact">
      <Hero
        image={IMAGES.contactHero}
        eyebrow={hero.eyebrow || "Let's talk"}
        title={hero.title || "Get in touch."}
        subtitle={hero.subtitle || "Call, text, email, or send a message below — whichever's easiest. A real person on our team will get back to you within the hour."}
        primaryCta={{ label: hero.primaryCtaLabel || "Book a Consultation", to: hero.primaryCtaTo || "/consultations" }}
        secondaryCta={{ label: hero.secondaryCtaLabel || "Call Now", to: hero.secondaryCtaTo || SITE.phoneHref }}
      />

      <section className="bg-[#f9f6f0] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <aside className="lg:col-span-5 space-y-8">
            <div>
              <span className="text-xs tracking-[0.24em] uppercase font-bold text-[#c85a3c]" data-testid="contact-section-eyebrow">
                {section.eyebrow || "Reach us"}
              </span>
              <h2 className="font-display text-3xl md:text-5xl font-black text-[#2c2a29] leading-[1.05] mt-4" data-testid="contact-section-title">
                {section.title || "Reach us any way that works."}
              </h2>
            </div>

            <ul className="space-y-4">
              {[
                {
                  icon: Phone,
                  label: info.phoneLabel || "Phone",
                  labelTestId: "contact-label-phone",
                  value: SITE.phone,
                  href: SITE.phoneHref,
                  testId: "contact-info-phone",
                },
                {
                  icon: Mail,
                  label: info.emailLabel || "Email",
                  labelTestId: "contact-label-email",
                  value: SITE.email,
                  href: SITE.emailHref,
                  testId: "contact-info-email",
                },
                {
                  icon: MapPin,
                  label: info.officeLabel || "Office",
                  labelTestId: "contact-label-office",
                  value: SITE.address,
                  testId: "contact-info-address",
                },
              ].map((c) => (
                <li
                  key={c.testId}
                  className="bg-[#fffdf9] rounded-2xl p-5 border border-[#e5e0d8] flex items-center gap-4"
                >
                  <span className="w-11 h-11 rounded-xl bg-[#c85a3c]/10 text-[#c85a3c] flex items-center justify-center">
                    <c.icon className="w-5 h-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#8b5d4e]" data-testid={c.labelTestId}>
                      {c.label}
                    </div>
                    {c.href ? (
                      <a
                        href={c.href}
                        data-testid={c.testId}
                        className="font-display text-lg font-bold text-[#2c2a29] hover:text-[#c85a3c] transition-colors"
                      >
                        {c.value}
                      </a>
                    ) : (
                      <span
                        data-testid={c.testId}
                        className="font-display text-lg font-bold text-[#2c2a29]"
                      >
                        {c.value}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            <div
              data-testid="business-hours"
              className="bg-[#2c2a29] text-[#f9f6f0] rounded-2xl p-6 md:p-8"
            >
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#c85a3c]" strokeWidth={2.4} />
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#c85a3c]" data-testid="business-hours-heading">
                  {hoursCopy.heading || "Business Hours"}
                </div>
              </div>
              <ul className="mt-5 space-y-2">
                {SITE.hours.map((h) => (
                  <li
                    key={h.day}
                    className="flex items-center justify-between text-sm border-b border-[#3d3a38] pb-2 last:border-0"
                  >
                    <span className="font-semibold">{h.day}</span>
                    <span className="text-[#c9c2b4]">{h.time}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-xs text-[#c9c2b4]" data-testid="business-hours-note">
                {hoursCopy.note || "24/7 emergency line available for burst pipes, no power, no heat."}
              </p>
            </div>
          </aside>

          <div className="lg:col-span-7">
            <ContactForm />

            {/* Map placeholder — styled tile with pin */}
            <div
              data-testid="map-placeholder"
              className="mt-8 relative rounded-3xl overflow-hidden border border-[#e5e0d8] aspect-[16/9] bg-[#e5e0d8]"
            >
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 30% 30%, #d4cdbe 0, #e5e0d8 40%), repeating-linear-gradient(45deg, #d9d3c6 0 2px, transparent 2px 24px), repeating-linear-gradient(-45deg, #d9d3c6 0 2px, transparent 2px 24px)",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2c2a29]/10 to-transparent" />
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
                <span className="w-12 h-12 rounded-full bg-[#c85a3c] text-[#fffdf9] flex items-center justify-center shadow-[0_10px_28px_rgba(200,90,60,0.5)] animate-pulse">
                  <MapPin className="w-6 h-6" strokeWidth={2.5} />
                </span>
                <div className="mt-3 bg-[#fffdf9] px-4 py-2 rounded-full text-xs font-bold text-[#2c2a29] shadow-md">
                  {SITE.address}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABanner
        image={IMAGES.finalCta}
        eyebrow={finalCta.eyebrow || "Ready when you are"}
        title={finalCta.title || "Prefer to just book?"}
        subtitle={finalCta.subtitle || "Skip the back and forth — book a free consultation now."}
        primary={{ label: finalCta.primaryLabel || "Book a Consultation", to: finalCta.primaryTo || "/consultations" }}
        secondary={{ label: finalCta.secondaryLabel || "Get Started", to: finalCta.secondaryTo || "/services" }}
      />
    </div>
  );
}
